import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	FormControl,
	InputLabel,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Select,
	Typography,
} from '@mui/material';
import Modal from './Modal';
import {
	fetchDowngradeSeatSelection,
	downgradeToBasic,
} from '../../Shared/Api';

const DowngradeSeatPickerModal = ({
	open,
	onClose,
	onSuccess,
	enqueueSnackbar,
}) => {
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [selection, setSelection] = useState(null);
	const [keepUserId, setKeepUserId] = useState('');

	useEffect(() => {
		if (!open) {
			return;
		}
		setLoading(true);
		fetchDowngradeSeatSelection()
			.then((response) => {
				setSelection(response);
				if (response?.single_manager?.id) {
					setKeepUserId(String(response.single_manager.id));
				} else if (response?.managers?.length === 1) {
					setKeepUserId(String(response.managers[0].id));
				}
			})
			.catch((error) => {
				enqueueSnackbar?.(error.message || 'Failed to load accounts.', {
					variant: 'error',
				});
				onClose?.();
			})
			.finally(() => setLoading(false));
	}, [open, enqueueSnackbar, onClose]);

	const managers = selection?.managers || [];
	const otherAccounts = selection?.other_accounts || [];
	const requiresManagerSelection = Boolean(
		selection?.requires_manager_selection,
	);

	const handleConfirm = async () => {
		if (!keepUserId) {
			enqueueSnackbar?.('Choose which manager account should stay active.', {
				variant: 'warning',
			});
			return;
		}

		setSubmitting(true);
		try {
			await downgradeToBasic(Number(keepUserId));
			enqueueSnackbar?.(
				'Downgrade scheduled for the end of your billing period.',
				{ variant: 'success' },
			);
			onSuccess?.();
			onClose?.();
		} catch (error) {
			enqueueSnackbar?.(error.message || 'Could not save seat selection.', {
				variant: 'error',
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle="Before downgrading to Basic"
			maxWidth="sm"
			useActionButtons
			actionButtons={
				<>
					<Button onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="warning"
						onClick={handleConfirm}
						disabled={loading || submitting}
					>
						{submitting ? 'Scheduling...' : 'Schedule downgrade'}
					</Button>
				</>
			}
		>
			{loading ? (
				<Typography variant="body2" color="text.secondary">
					Loading accounts...
				</Typography>
			) : (
				<Box sx={{ mt: 1 }}>
					<Alert severity="info" sx={{ mb: 2 }}>
						Basic allows 1 active seat. Choose which account should stay active
						when the downgrade takes effect at the end of your billing period.
						All accounts stay active with full Pro access until then.
					</Alert>

					{requiresManagerSelection ? (
						<FormControl fullWidth sx={{ mb: 2 }}>
							<InputLabel id="keep-manager-label">
								Which account should stay active on Basic?
							</InputLabel>
							<Select
								labelId="keep-manager-label"
								label="Which account should stay active on Basic?"
								value={keepUserId}
								onChange={(event) => setKeepUserId(event.target.value)}
							>
								{managers.map((manager) => (
									<MenuItem key={manager.id} value={String(manager.id)}>
										{manager.email} (Manager)
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : selection?.single_manager ? (
						<Typography variant="body2" sx={{ mb: 2 }}>
							Account <strong>{selection.single_manager.email}</strong> will be
							the only active account after the downgrade. Until then, these
							accounts stay active. When the downgrade happens, unselected
							accounts will be deactivated (not deleted) and can be restored or
							removed when you upgrade to Pro again:
						</Typography>
					) : null}

					{otherAccounts.length > 0 ? (
						<List dense>
							{otherAccounts.map((account) => (
								<ListItem key={account.id} disablePadding>
									<ListItemText
										primary={account.email}
										secondary={account.role}
									/>
								</ListItem>
							))}
						</List>
					) : managers.length > 1 ? (
						<Typography variant="body2" color="text.secondary">
							Non-selected manager accounts will also be deactivated when the
							downgrade takes effect.
						</Typography>
					) : null}
				</Box>
			)}
		</Modal>
	);
};

export default DowngradeSeatPickerModal;
